// src/oura/components/visibility/ReportCard.tsx
//
// The report under the tiles: Questions, Competitors and Sources as tabs on
// one card, the shape of Search Console's Performance report.

import { forwardRef, type ReactNode } from "react";
import { FOCUS_RING, Seg, Surface, TYPE } from "@oura/ui";
import { cn } from "../../lib/cn";

export type ReportTab = "questions" | "competitors" | "sources";

const LABEL: Record<ReportTab, string> = { questions: "Questions", competitors: "Competitors", sources: "Sources" };

/**
 * A segmented track that fills the width below 640px, each item sized to its
 * words, in 12px with tighter padding. The track scrolls sideways with its bar
 * hidden, so on a phone the last item sat behind the edge with nothing to say
 * it was there. `max-sm:` rather than a base class, so it outranks the
 * track's own `inline-flex`.
 */
export const SEG_FILL_ON_PHONE =
    "max-sm:flex max-sm:w-full max-sm:[&>button]:min-w-0 max-sm:[&>button]:flex-auto max-sm:[&>button]:justify-center max-sm:[&>button]:gap-1 max-sm:[&>button]:px-1.5 max-sm:[&>button]:text-xs";

export const ReportCard = forwardRef<HTMLDivElement, {
    tab: ReportTab;
    counts: Record<ReportTab, number>;
    onTab: (tab: ReportTab) => void;
    /** One line on what the tab shows. */
    /** A line, or a node (the questions table's key, portalled in). */
    hint: ReactNode;
    /** Staff controls for the tab on screen (Export CSV, Recompute). */
    controls?: ReactNode;
    /** Beside the tabs: which version's questions the card shows. */
    marker?: ReactNode;
    children: ReactNode;
}>(function ReportCard({ tab, counts, onTab, hint, controls, marker, children }, ref) {
    return (
        // scroll-mt clears nothing sticky today; it keeps the card's head off
        // the very edge when a tile or a grid cell scrolls the page to it.
        <div ref={ref} className="scroll-mt-4">
            <Surface flush>
                <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-4 sm:px-5">
                    <div className="flex flex-wrap items-center gap-3 max-sm:w-full">
                        <Seg<ReportTab>
                            as="tablist"
                            ariaLabel="Report"
                            items={(Object.keys(LABEL) as ReportTab[]).map((k) => ({ key: k, label: LABEL[k], count: counts[k] }))}
                            value={tab}
                            onChange={onTab}
                            className={SEG_FILL_ON_PHONE}
                        />
                        {marker}
                    </div>
                    <div className={cn(TYPE.tc, "flex flex-wrap items-center gap-3 text-gray-400")}>
                        {hint && <span>{hint}</span>}
                        {controls}
                    </div>
                </div>
                <div role="tabpanel" aria-label={LABEL[tab]}>
                    {children}
                </div>
            </Surface>
        </div>
    );
});

/**
 * The question version the card shows, once, in its header: the rows no longer
 * carry it each (Jack, 22 Oct 2026). On an earlier scan it says which, with
 * the way back to the newest.
 */
export function VersionMarker({ name, checked, onBack }: { name: string; checked: string | null; onBack?: () => void }) {
    return (
        <span className={cn(TYPE.tc, "inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-500")}>
            <span className="inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-md bg-blue-50 px-2 text-xs font-semibold text-blue-700 ring-1 ring-inset ring-blue-200">
                <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />
                {name} questions
            </span>
            {checked && <span className="whitespace-nowrap">checked {checked}</span>}
            {onBack && (
                <button type="button" onClick={onBack} className={cn("whitespace-nowrap rounded font-medium text-blue-600 hover:text-blue-700", FOCUS_RING)}>
                    Back to latest
                </button>
            )}
        </span>
    );
}
